/**
 * Client Portal — Assessment Requests page.
 * Allows clients to book new assessment requests and track their status.
 * Requests are sent to the Engagement Manager for review.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Grid, Card, CardContent,
} from '@mui/material';
import { Add, Send, CheckCircle, Cancel, HourglassEmpty, Assignment, Delete } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';
import PortalTooltip from '../../../components/PortalTooltip';

interface AssessmentCategory {
  category: string;
  domains: string[];
}

interface AssessmentRequestItem {
  id: string;
  category: string;
  domain: string;
  business_justification: string;
  priority: string;
  status: string;
  review_notes: string | null;
  created_at: string | null;
  reviewed_at: string | null;
}

const STATUS_CONFIG: Record<string, { color: 'default' | 'warning' | 'success' | 'error' | 'info'; icon: React.ReactNode }> = {
  pending:   { color: 'warning', icon: <HourglassEmpty fontSize="small" /> },
  approved:  { color: 'success', icon: <CheckCircle fontSize="small" /> },
  rejected:  { color: 'error',   icon: <Cancel fontSize="small" /> },
  completed: { color: 'info',    icon: <CheckCircle fontSize="small" /> },
};

const PRIORITY_COLORS: Record<string, 'default' | 'warning' | 'error' | 'info'> = {
  low: 'default', normal: 'info', high: 'warning', urgent: 'error',
};

const AssessmentRequests: React.FC = () => {
  const [requests, setRequests] = useState<AssessmentRequestItem[]>([]);
  const [categories, setCategories] = useState<AssessmentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Form state
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [justification, setJustification] = useState('');
  const [priority, setPriority] = useState('normal');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, catRes] = await Promise.all([
        authFetch('/api/client/assessment-requests'),
        authFetch('/api/client/assessment-domains'),
      ]);

      if (!reqRes.ok) {
        const err = await reqRes.json().catch(() => ({}));
        throw new Error(err.error || `Requests failed: ${reqRes.status}`);
      }
      if (!catRes.ok) {
        const err = await catRes.json().catch(() => ({}));
        throw new Error(err.error || `Domains failed: ${catRes.status}`);
      }

      const reqData = await reqRes.json();
      const catData = await catRes.json();
      if (reqData.success) setRequests(reqData.requests);
      if (catData.success && catData.categories) setCategories(catData.categories);
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLastRefreshed(new Date());
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const domainsForCategory = categories.find(c => c.category === selectedCategory)?.domains || [];

  const handleSubmit = async () => {
    if (!selectedCategory || !selectedDomain) return;
    setSubmitting(true);
    try {
      const res = await authFetch('/api/client/assessment-requests', {
        method: 'POST',
        body: JSON.stringify({
          category: selectedCategory,
          domain: selectedDomain,
          business_justification: justification,
          priority,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(data.message || 'Request submitted successfully');
        setDialogOpen(false);
        setSelectedCategory('');
        setSelectedDomain('');
        setJustification('');
        setPriority('normal');
        fetchData();
      } else {
        setError(data.error || 'Failed to submit request');
      }
    } catch {
      setError('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this request from your history?')) return;
    setDeleting(id);
    try {
      const res = await authFetch(`/api/client/assessment-requests/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setSuccess('Request removed');
        fetchData();
      } else {
        setError(data.error || 'Failed to delete request');
      }
    } catch {
      setError('Failed to delete request');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <PortalTooltip tooltipKey="client.page.requests" showIcon>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
              Assessment Requests
            </Typography>
          </PortalTooltip>
          <Typography variant="body2" color="text.secondary">
            Request new assessments for your organization. Your Engagement Manager will review and approve requests.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RefreshButton onRefresh={fetchData} loading={loading} lastRefreshed={lastRefreshed} />
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)} size="large">
            New Request
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="warning.main">{pendingCount}</Typography>
              <Typography variant="body2" color="text.secondary">Pending Review</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="success.main">{approvedCount}</Typography>
              <Typography variant="body2" color="text.secondary">Approved</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="primary">{requests.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total Requests</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Requests Table */}
      {requests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Assignment sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No assessment requests yet. Click "New Request" to get started.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>EM Response</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => {
                const statusCfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
                return (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Chip label={r.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{r.domain}</TableCell>
                    <TableCell>
                      <Chip label={r.priority} size="small" color={PRIORITY_COLORS[r.priority] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip icon={statusCfg.icon as React.ReactElement} label={r.status} size="small" color={statusCfg.color} />
                    </TableCell>
                    <TableCell>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      {r.review_notes || (r.reviewed_at ? 'Reviewed' : '—')}
                    </TableCell>
                    <TableCell>
                      {(r.status === 'approved' || r.status === 'completed' || r.status === 'rejected') ? (
                        <Button
                          size="small"
                          color="error"
                          startIcon={deleting === r.id ? <CircularProgress size={14} /> : <Delete />}
                          onClick={() => handleDelete(r.id)}
                          disabled={deleting === r.id}
                        >
                          Remove
                        </Button>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* New Request Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request New Assessment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the assessment category and domain. Your Engagement Manager will review this request.
          </Typography>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Assessment Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Assessment Category"
              onChange={(e) => { setSelectedCategory(e.target.value); setSelectedDomain(''); }}
            >
              {categories.map((c) => (
                <MenuItem key={c.category} value={c.category}>{c.category}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={!selectedCategory}>
            <InputLabel>Assessment Domain</InputLabel>
            <Select
              value={selectedDomain}
              label="Assessment Domain"
              onChange={(e) => setSelectedDomain(e.target.value)}
            >
              {domainsForCategory.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={priority} label="Priority" onChange={(e) => setPriority(e.target.value)}>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Business Justification"
            placeholder="Briefly describe why this assessment is needed..."
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={handleSubmit}
            disabled={!selectedCategory || !selectedDomain || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessmentRequests;
